import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Mic, BookOpen, Anchor, Sparkles, ArrowDown, ChevronRight } from 'lucide-react';
import ThemeToggle from '../components/ThemeToggle';
import SiteFooter from '../components/SiteFooter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * LandingPage — marketing entry point.
 *
 * Design brief captured from the user (verbatim):
 *   • Tone: "poetic yet approachable".
 *   • CTA hierarchy: "Sign in should remain dominant but beta request
 *     should be easy to see" — no more collapse-behind-a-link; the beta
 *     form is inline in the hero alongside Sign In.
 *   • Metaphor: "SAGE isn't as much a mirror as it is a path" — every
 *     section frames SAGE as a companion on a journey, not a reflection.
 *
 * Sections (top → bottom):
 *   1. Hero — misty tree, headline, primary Sign In, inline beta form.
 *   2. What SAGE is — single meaningful statement.
 *   3. Four pillars — Live Coaching · Learn Mode · Knowledge Anchors · Memory.
 *   4. The Path — 3-step how-it-works.
 *   5. Who walks this path — invitational framing.
 *   6. Closing CTA — echoes the hero for scrollers.
 *   7. Footer — Est. MMXXVI + a small existing-user sign-in link.
 *
 * Reused visual language:
 *   • `.threshold-button` for primary CTAs (frosted stone doorway).
 *   • `.frost-button`     for secondary utility buttons.
 *   • Cormorant Garamond for headings, sans for body.
 *   • Tree image is same-origin (`/landing-tree-fog.jpg`) — bundled with
 *     the build so it renders in both preview + production.
 */
const LandingPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // For the closing-CTA form (separate state so it can submit independently
  // of the hero form, and so a scroller who's already submitted at top
  // still sees a fresh form when they reach the bottom).
  const [emailBottom, setEmailBottom] = useState('');
  const [loadingBottom, setLoadingBottom] = useState(false);
  const [submittedBottom, setSubmittedBottom] = useState(false);

  const nextSectionRef = useRef(null);

  // Fade-in-on-scroll for content sections. Cheap IntersectionObserver
  // pattern — one observer, one class toggled. Kept in this component
  // instead of extracting a hook because it's a single-page-file feature.
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('reveal-visible');
          }
        });
      },
      { threshold: 0.12 },
    );
    document.querySelectorAll('[data-reveal]').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const submitBeta = async (value, setSubmittedFn, setLoadingFn) => {
    if (!value) {
      toast.error('Email is required');
      return;
    }
    setLoadingFn(true);
    try {
      await axios.post(`${API}/beta/request`, { email: value });
      setSubmittedFn(true);
      navigate('/welcome', { state: { email: value } });
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to submit request';
      toast.error(message);
    } finally {
      setLoadingFn(false);
    }
  };

  return (
    <div className="min-h-screen relative bg-obsidian text-zinc-100" data-testid="landing-page">
      {/* Global noise + subtle grain overlays for the whole scroll. */}
      <div className="overlay-noise fixed inset-0 pointer-events-none z-0" />

      {/* Floating theme toggle — top-right corner, low profile so it
          never fights the doorway/brand moment. */}
      <div className="fixed top-5 right-5 z-30">
        <ThemeToggle size="sm" className="border border-white/14 bg-stone-950/40 backdrop-blur-sm rounded-sm" />
      </div>

      {/* ============================================================
           HERO — the doorway. Full-viewport tree, headline, two CTAs.
           ============================================================ */}
      <section
        className="relative min-h-screen flex flex-col items-center justify-center px-6 py-16 overflow-hidden"
        data-testid="landing-hero"
      >
        {/* Tree background — bundled asset, same-origin so it works in
             preview + production without depending on external hosts. */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "linear-gradient(180deg, rgba(10,10,10,0.35) 0%, rgba(10,10,10,0.72) 55%, rgba(10,10,10,0.96) 100%), url('/landing-tree-fog.jpg')",
          }}
          data-testid="landing-tree-bg"
        />

        {/* Existing-user sign-in tucked into the header — a small quiet
             link so returning members don't have to scan the whole page. */}
        <div className="absolute top-6 right-6 z-20">
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="text-[11px] font-mono uppercase tracking-widest text-zinc-400 hover:text-white transition-colors"
            data-testid="header-signin-link"
          >
            Already invited? Sign in →
          </button>
        </div>

        <div className="relative z-10 w-full max-w-3xl text-center animate-in fade-in duration-1000">
          {/* Wordmark */}
          <h1
            className="text-7xl md:text-9xl font-light tracking-tight text-zinc-100 mb-6"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
            data-testid="landing-title"
          >
            SAGE
          </h1>

          {/* Poetic yet approachable tagline. "Path" metaphor throughout. */}
          <p
            className="text-2xl md:text-3xl font-light text-zinc-200 mb-4 leading-relaxed"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
            data-testid="landing-tagline"
          >
            The path is yours.
            <br className="hidden md:block" />
            <span className="italic text-zinc-300"> SAGE walks it with you.</span>
          </p>

          <p className="text-sm md:text-base font-light text-zinc-400 tracking-wide mb-14 max-w-xl mx-auto">
            A voice-first AI companion for reflection and learning — shaped by
            the books, teachers, and questions you bring.
          </p>

          {/* CTA row — Sign In dominant, Beta Access visible next to it.
               Per user brief: no more collapsed "click to reveal" beta link. */}
          <div
            className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-4 max-w-2xl mx-auto"
            data-testid="hero-cta-row"
          >
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="threshold-button rounded-sm px-8 py-4 text-lg flex-1"
              data-testid="landing-signin-button"
              aria-label="Sign in to SAGE"
            >
              <span>Sign In</span>
              <span className="threshold-button-arrow" aria-hidden="true">→</span>
            </button>

            {/* Inline beta form — collapsed to a small pill when not focused,
                 expands in place on click. Still on-page (no scroll needed),
                 which is what the user meant by "easy to see". */}
            <BetaInlineForm
              variant="hero"
              email={email}
              setEmail={setEmail}
              loading={loading}
              submitted={submitted}
              onSubmit={() => submitBeta(email, setSubmitted, setLoading)}
            />
          </div>
        </div>

        {/* Scroll cue */}
        <button
          type="button"
          onClick={() =>
            nextSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }
          className="absolute bottom-10 left-1/2 -translate-x-1/2 text-zinc-500 hover:text-zinc-300 transition-colors flex flex-col items-center gap-2"
          aria-label="Scroll to learn more"
          data-testid="scroll-cue"
        >
          <span className="text-[10px] font-mono uppercase tracking-widest">Read on</span>
          <ArrowDown className="w-4 h-4 animate-bounce-slow" />
        </button>
      </section>

      {/* ============================================================
           WHAT SAGE IS — one meaningful statement on obsidian.
           ============================================================ */}
      <section
        ref={nextSectionRef}
        className="relative py-32 px-6 border-t border-white/5"
        data-testid="landing-manifesto"
      >
        <div className="max-w-3xl mx-auto text-center" data-reveal>
          <p
            className="text-[11px] font-mono uppercase tracking-[0.35em] text-amber-200/60 mb-10"
          >
            What SAGE is
          </p>
          <p
            className="text-3xl md:text-4xl font-light text-zinc-200 leading-relaxed"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Not a mirror. A <span className="italic text-white">path</span>.
            <br />
            <span className="text-zinc-400">
              A quiet, voice-first companion that carries the wisdom you choose,
              and walks with you toward the person you&rsquo;re becoming.
            </span>
          </p>
        </div>
      </section>

      {/* ============================================================
           FOUR PILLARS — control + flexibility framing.
           Each pillar is one card; hover raises the border to catch light.
           ============================================================ */}
      <section
        className="relative py-24 px-6 border-t border-white/5"
        data-testid="landing-pillars"
      >
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16" data-reveal>
            <p className="text-[11px] font-mono uppercase tracking-[0.35em] text-amber-200/60 mb-4">
              What you can do
            </p>
            <h2
              className="text-4xl md:text-5xl font-light text-zinc-100"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Take control of your inner work.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-reveal>
            <Pillar
              icon={<Sparkles className="w-5 h-5" aria-hidden="true" />}
              eyebrow="Live Coaching"
              title="Speak. Be heard. Move."
              body="Voice-to-voice sessions with a coach that listens without an agenda. Bring the moment you're in — SAGE meets you there."
              testid="pillar-coaching"
            />
            <Pillar
              icon={<BookOpen className="w-5 h-5" aria-hidden="true" />}
              eyebrow="Learn Mode"
              title="Socratic tutoring, on your material."
              body="Upload a book, a paper, or your own notes. SAGE builds a plan and teaches you one idea at a time, adapting to what you know."
              testid="pillar-learn"
            />
            <Pillar
              icon={<Anchor className="w-5 h-5" aria-hidden="true" />}
              eyebrow="Knowledge Anchors"
              title="Bring the voices that shape you."
              body="Books, teachers, principles — whatever guides your thinking. Drop them in; SAGE weaves them into how it responds, in the proportions you choose."
              testid="pillar-anchors"
            />
            <Pillar
              icon={<Mic className="w-5 h-5" aria-hidden="true" />}
              eyebrow="Memory that grows"
              title="Return to yourself, session after session."
              body="Every conversation becomes searchable, every win kept. Come back a week later and pick up where you left off — SAGE remembers."
              testid="pillar-memory"
            />
          </div>
        </div>
      </section>

      {/* ============================================================
           THE PATH — 3-step how-it-works.
           ============================================================ */}
      <section
        className="relative py-24 px-6 border-t border-white/5"
        data-testid="landing-path"
      >
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-20" data-reveal>
            <p className="text-[11px] font-mono uppercase tracking-[0.35em] text-amber-200/60 mb-4">
              How the path opens
            </p>
            <h2
              className="text-4xl md:text-5xl font-light text-zinc-100"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Three small crossings.
            </h2>
          </div>

          <ol
            className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-4"
            data-reveal
          >
            <PathStep
              n="I"
              title="Bring your voices"
              body="Upload the books, teachers, and principles that guide you. These become the shape of every conversation."
              testid="step-1"
            />
            <PathStep
              n="II"
              title="Speak or study"
              body="Open Live Coaching to talk through a moment. Open Learn Mode to master a topic. Both are voice-first, both are yours."
              testid="step-2"
            />
            <PathStep
              n="III"
              title="Return anytime"
              body="Every session is remembered. Search, revisit, or step back in. The path is always waiting."
              testid="step-3"
            />
          </ol>
        </div>
      </section>

      {/* ============================================================
           WHO WALKS THIS PATH — invitational, not exclusionary.
           ============================================================ */}
      <section
        className="relative py-24 px-6 border-t border-white/5"
        data-testid="landing-audience"
      >
        <div className="max-w-3xl mx-auto text-center" data-reveal>
          <p className="text-[11px] font-mono uppercase tracking-[0.35em] text-amber-200/60 mb-6">
            Who this is for
          </p>
          <p
            className="text-2xl md:text-3xl font-light text-zinc-200 leading-relaxed"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Seekers. Builders. Students of the long game.
          </p>
          <p className="text-base text-zinc-400 mt-6 leading-relaxed">
            SAGE is invitation-only during beta — small enough to shape with you,
            deep enough to hold the questions you actually carry.
          </p>
        </div>
      </section>

      {/* ============================================================
           CLOSING CTA — echoes the hero for scroll-through readers.
           ============================================================ */}
      <section
        className="relative py-32 px-6 border-t border-white/5"
        data-testid="landing-closing-cta"
      >
        <div className="max-w-2xl mx-auto text-center" data-reveal>
          <h2
            className="text-4xl md:text-5xl font-light text-zinc-100 mb-6"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Step onto the path.
          </h2>
          <p className="text-base text-zinc-400 mb-10 max-w-lg mx-auto leading-relaxed">
            Beta is opening in small groups. Leave your email and we&rsquo;ll send an
            invitation when the next threshold opens.
          </p>

          <div
            className="flex flex-col sm:flex-row items-stretch justify-center gap-3 max-w-xl mx-auto"
            data-testid="closing-cta-row"
          >
            <BetaInlineForm
              variant="closing"
              email={emailBottom}
              setEmail={setEmailBottom}
              loading={loadingBottom}
              submitted={submittedBottom}
              onSubmit={() =>
                submitBeta(emailBottom, setSubmittedBottom, setLoadingBottom)
              }
            />
          </div>

          <p className="mt-8 text-xs text-zinc-500">
            Already invited?{' '}
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="underline underline-offset-4 hover:text-zinc-300 transition-colors"
              data-testid="closing-signin-link"
            >
              Sign in here
            </button>
            .
          </p>
        </div>
      </section>

      {/* ============================================================
           FOOTER
           ============================================================ */}
      <footer className="relative py-10 px-6 border-t border-white/5">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs font-mono uppercase tracking-widest text-zinc-600">
            SAGE · Est. MMXXVI
          </p>
          <p className="text-xs font-mono uppercase tracking-widest text-zinc-700">
            The path is yours.
          </p>
        </div>
      </footer>
      <SiteFooter />
    </div>
  );
};

/**
 * BetaInlineForm — a compact inline email capture that lives in the
 * hero CTA row and again in the closing CTA. Rendered as a form so
 * pressing Enter submits; on success it flips to a small confirmation.
 * The `variant` prop only affects width (hero: sits next to Sign In,
 * closing: full-width standalone).
 */
/**
 * BetaInlineForm — a compact inline email capture that lives in the
 * hero CTA row and again in the closing CTA. Rendered as a form so
 * pressing Enter submits; on success it flips to a small confirmation.
 *
 * `variant='hero'` uses **progressive reveal**: initially it renders
 * ONLY a "Request Beta Access →" button; tapping it inline-expands
 * the button into the labelled email form in-place (no modal, no
 * scroll). This keeps the hero aesthetic calm and surfaces the form
 * only when the user signals intent.
 *
 * `variant='closing'` always renders the form (fully expanded) because
 * at the bottom of the page the user has already decided — there's no
 * value in asking a second time.
 */
const BetaInlineForm = ({ variant, email, setEmail, loading, submitted, onSubmit }) => {
  const isHero = variant === 'hero';
  // Hero starts collapsed; closing always open. Local state only — once
  // the user expands we leave it open until success or page reload.
  const [expanded, setExpanded] = useState(!isHero);
  const inputRef = useRef(null);

  // When the hero form expands, autofocus the input so the keyboard
  // appears on mobile and desktop users can start typing immediately.
  useEffect(() => {
    if (expanded && isHero && inputRef.current && !submitted) {
      // Small timeout so the expand animation starts first — focusing
      // mid-transition prevents jank on iOS Safari.
      const t = setTimeout(() => inputRef.current?.focus(), 60);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [expanded, isHero, submitted]);

  if (submitted) {
    return (
      <div
        className={`${isHero ? 'flex-1' : 'w-full'} bg-white/[0.03] border border-emerald-400/30 rounded-sm px-6 py-4 text-emerald-200 text-sm text-center`}
        data-testid={`beta-success-${variant}`}
      >
        ✓ Request received. You&rsquo;re on the list.
      </div>
    );
  }

  // Collapsed state — hero only. Renders as a threshold-styled button
  // sized to match the Sign In sibling. Tapping it flips `expanded`.
  if (isHero && !expanded) {
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        className="frost-button rounded-sm px-8 py-4 text-lg flex-1"
        data-testid="beta-reveal-hero"
        aria-expanded="false"
        aria-controls="beta-form-hero"
      >
        <span>Request Beta Access</span>
        <ChevronRight className="w-5 h-5" aria-hidden="true" />
      </button>
    );
  }

  return (
    <form
      id={isHero ? 'beta-form-hero' : undefined}
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      className={`${isHero ? 'flex-1' : 'w-full'} relative`}
      data-testid={`beta-form-${variant}`}
    >
      <div className="flex items-stretch bg-white/[0.03] border border-white/15 hover:border-white/30 focus-within:border-white/50 rounded-sm transition-colors overflow-hidden">
        <input
          ref={inputRef}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => {
            // Hero only: pressing Escape on an empty field collapses
            // the form back to the button so the hero regains its calm.
            if (isHero && e.key === 'Escape' && !email) {
              setExpanded(false);
            }
          }}
          placeholder="your email"
          className="flex-1 bg-transparent border-0 outline-none text-white placeholder:text-zinc-500 text-base font-light px-4 py-4 focus:ring-0 min-w-0"
          data-testid={`beta-email-input-${variant}`}
          disabled={loading}
          aria-label="Email for beta access"
        />
        <button
          type="submit"
          disabled={loading}
          className="frost-button rounded-none border-0 border-l border-white/10 px-5 text-sm whitespace-nowrap disabled:opacity-50"
          data-testid={`beta-submit-${variant}`}
        >
          {loading ? (
            <span>Sending…</span>
          ) : (
            <>
              <span>Request</span>
              <ChevronRight className="w-4 h-4" aria-hidden="true" />
            </>
          )}
        </button>
      </div>
    </form>
  );
};

/**
 * Pillar — a card in the "What you can do" grid. Frosted border, icon
 * eyebrow, poetic title, plain-language body. Hover lifts the border
 * to catch a bit more light.
 */
const Pillar = ({ icon, eyebrow, title, body, testid }) => (
  <div
    className="group relative bg-white/[0.02] border border-white/10 hover:border-white/25 rounded-sm p-8 transition-all duration-500"
    data-testid={testid}
  >
    <div className="flex items-center gap-3 mb-5 text-amber-200/80 group-hover:text-amber-200 transition-colors">
      <span className="w-8 h-8 rounded-sm border border-white/15 flex items-center justify-center">
        {icon}
      </span>
      <span className="text-[11px] font-mono uppercase tracking-[0.25em]">
        {eyebrow}
      </span>
    </div>
    <h3
      className="text-2xl font-light text-white mb-3 leading-snug"
      style={{ fontFamily: 'Cormorant Garamond, serif' }}
    >
      {title}
    </h3>
    <p className="text-sm text-zinc-400 leading-relaxed">{body}</p>
  </div>
);

/**
 * PathStep — one of the three crossings in the "how the path opens"
 * section. Roman numeral badge, short title, short body.
 */
const PathStep = ({ n, title, body, testid }) => (
  <li className="text-center md:text-left px-2" data-testid={testid}>
    <div
      className="inline-flex items-center justify-center w-12 h-12 rounded-full border border-amber-200/40 text-amber-200/90 mb-6"
      style={{ fontFamily: 'Cormorant Garamond, serif' }}
      aria-hidden="true"
    >
      <span className="text-lg">{n}</span>
    </div>
    <h3
      className="text-2xl font-light text-white mb-3"
      style={{ fontFamily: 'Cormorant Garamond, serif' }}
    >
      {title}
    </h3>
    <p className="text-sm text-zinc-400 leading-relaxed">{body}</p>
  </li>
);

export default LandingPage;
